import { LogLevel, Modal, ModalType, PendingButton, showBanner } from '@sprint/sprint-react-components';
import React, { FunctionComponent, useState } from 'react';
import ReconcileRepository, { ReconcileEntity, ReconcileOperation, ReconcileRequest } from '../../Api/ReconcileRepository';
import { SimpleFilterRequest } from '../../Components/Filters';

interface Props {
    entity: ReconcileEntity;
    operation: ReconcileOperation;
    rows: { id: number }[];
    close: () => void;
    everythingSelected?: boolean;
    totalMatching?: number;
    filter?: SimpleFilterRequest;
}

const ReconcileModal: FunctionComponent<Props> = (props: Props) => {
    const repository = new ReconcileRepository();
    const ids = props.rows.map((row) => row.id);

    const isUpdate = props.operation === 'update';
    const isOrganisation = props.entity === 'organisation';
    const nounPlural = isOrganisation ? 'organisations' : 'contacts';
    const title = isUpdate ? 'Update from Education Data' : 'Link to Education Data';
    const action = isUpdate ? 'updating' : 'linking';

    // Offer "all matching" only when the current page selection is maxed (or a select-all
    // is active) AND there are genuinely more matching records than are selected.
    const canRunAllMatching =
        !!props.filter &&
        typeof props.totalMatching === 'number' &&
        props.totalMatching > ids.length &&
        (props.everythingSelected === true || ids.length >= 100);

    const [runAllMatching, setRunAllMatching] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const useAllMatching = canRunAllMatching && runAllMatching;

    const onSubmit = (): void => {
        setSubmitting(true);
        const request: ReconcileRequest = useAllMatching
            ? { entity: props.entity, operation: props.operation, mode: 'all_matching', filter: props.filter }
            : { entity: props.entity, operation: props.operation, mode: 'selected', ids };

        repository
            .reconcile(request)
            .then((result) => {
                const count = result?.count ?? (useAllMatching ? props.totalMatching ?? 0 : ids.length);
                showBanner({
                    message: `Started ${action} ${count} ${nounPlural}. We'll let you know when it's done.`,
                    level: LogLevel.SUCCESS,
                });
                props.close();
            })
            .catch((err) => {
                showBanner({
                    message: `Failed to start ${action} - ` + (err?.message ?? err),
                    level: LogLevel.ERROR,
                });
                setSubmitting(false);
            });
    };

    const updateCopy = isOrganisation
        ? "Overwrite the selected linked organisations' own details (name, address and phone) with the latest " +
          "Education Data. Tags and custom fields are kept. This runs in the background and can't be undone."
        : "Overwrite the selected linked contacts' own details (name, email and job title) with the latest " +
          "Education Data. Tags and custom fields are kept. This runs in the background and can't be undone.";

    const linkCopy = isOrganisation
        ? 'Match your organisations against your Education Data and link the ones we can identify by name and ' +
          'postcode or phone. Already-linked organisations and those with no match are skipped. Linking runs in the background.'
        : 'Match your contacts against your Education Data and link the ones we can identify by email. ' +
          'Already-linked contacts and those with no match are skipped. Linking runs in the background.';

    return (
        <Modal
            isOpen={true}
            type={ModalType.YESNO}
            title={title}
            close={() => props.close()}
            footerOverride={
                <>
                    <PendingButton variant="primary" pending={submitting} onClick={onSubmit}>
                        Yes
                    </PendingButton>
                    <PendingButton variant="default" pending={false} disabled={submitting} onClick={() => props.close()}>
                        No
                    </PendingButton>
                </>
            }
        >
            <p>{isUpdate ? updateCopy : linkCopy}</p>
            {canRunAllMatching && (
                <div style={{ marginTop: '0.75rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <input
                            type="radio"
                            name="reconcile-scope"
                            checked={!runAllMatching}
                            onChange={() => setRunAllMatching(false)}
                        />
                        <span>
                            The {ids.length} selected {nounPlural}
                        </span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input
                            type="radio"
                            name="reconcile-scope"
                            checked={runAllMatching}
                            onChange={() => setRunAllMatching(true)}
                        />
                        <span>
                            All {props.totalMatching} {nounPlural} matching your current filters
                        </span>
                    </label>
                </div>
            )}
        </Modal>
    );
};

export default ReconcileModal;
